// src/pages/lkp/LookupReferenceReview.tsx
//
// Step 2 — client-entity view with Step-1 cross-certification.
// LEFT: the lookup entities the SME uploaded (client domains) + a section for Step-1
//       lookups that have no client data. Filter by how Step 1 and Step 2 certify a column.
// RIGHT: for the selected domain, the matched physical column(s) shown by DISPLAY name,
//       with matching justification, a Step-1 badge (detected? status? entity?), and the
//       codes/display from BOTH sides (data vs Excel). Actions: accept / reject / remap.

import React, { useMemo, useState } from "react";
import {
  lookup_saveReview,
  type AuthoritativeBlock, type LookupCandidatesBlock, type MappingReview,
} from "../api/lkpAPI";

interface Props {
  entityId: string; auth: AuthoritativeBlock | null; candidates: LookupCandidatesBlock | null;
  logicalFields: string[]; busy: boolean;
  onSaved: () => void | Promise<void>; onReconcile: () => void; onGoDiscover: () => void;
}
const keyOf = (t?: string, c?: string) => `${(t || "").trim()}.${(c || "").trim()}`.toUpperCase();
const norm = (v: any) => String(v ?? "").trim().toLowerCase();
type Cert = "both" | "step2only" | "step1only" | "none";
type Filter = "all" | Cert | "conflict" | "unmatched";

export default function LookupReferenceReview({
  entityId, auth, candidates, logicalFields, busy, onSaved, onReconcile, onGoDiscover,
}: Props) {
  const defs = (auth?.definitions || []) as any[];
  const domains = ((auth as any)?.client_domains || []) as Array<{ logical_field: string; code_count: number; code_to_description: Record<string, string> }>;
  const [sel, setSel] = useState<{ type: "domain" | "col"; id: string } | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [bucket, setBucket] = useState<"all" | "review" | "accepted" | "rejected">("all");
  const [q, setQ] = useState("");
  const [pending, setPending] = useState<string | null>(null);
  const [remapFor, setRemapFor] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ m: string; e?: boolean } | null>(null);
  const note = (m: string, e = false) => { setFlash({ m, e }); setTimeout(() => setFlash(null), 3500); };

  // ---- indexes ----
  const defsByLogical = useMemo(() => {
    const m = new Map<string, any[]>();
    defs.forEach((d) => { if (d.logical_field) (m.get(d.logical_field) || m.set(d.logical_field, []).get(d.logical_field))!.push(d); });
    return m;
  }, [defs]);
  const defByKey = useMemo(() => { const m = new Map<string, any>(); defs.forEach((d) => m.set(keyOf(d.table, d.column), d)); return m; }, [defs]);
  const allColumns = useMemo(() => defs.map((d) => ({ key: keyOf(d.table, d.column), label: d.business_name || d.column, table: d.table, column: d.column })), [defs]);

  const cand = useMemo(() => {
    const m = new Map<string, { status: string; entity: string; observed: string[]; fam: string; bn?: string }>();
    (candidates?.items || []).forEach((c: any) => {
      const ref = c.family === "lookup_field" ? c.field : c.family === "code_description_pair" ? c.code : null;
      if (!ref) return;
      m.set(keyOf(ref.table, ref.field), { status: c.status || "PROPOSED", entity: c.entity_name || "", observed: c.observed_values || Object.keys(c.code_to_description || {}), fam: c.family, bn: c.business_name });
    });
    return m;
  }, [candidates]);

  const certOf = (colKey: string): { cert: Cert; inS1: boolean; inS2: boolean; s1status?: string; s1entity?: string } => {
    const c = cand.get(colKey);
    const inS1 = !!c && c.status !== "REJECTED";
    const d = defByKey.get(colKey);
    const inS2 = !!(d && d.logical_field);
    const cert: Cert = inS1 && inS2 ? "both" : inS2 ? "step2only" : inS1 ? "step1only" : "none";
    return { cert, inS1, inS2, s1status: c?.status, s1entity: c?.entity };
  };
  const reviewOf = (colKey: string): "review" | "accepted" | "rejected" => {
    const d = defByKey.get(colKey); const st = String(d?.review?.status || "").toUpperCase();
    if (d?.review?.confirmed || st === "RESOLVED") return "accepted";
    if (st === "REJECTED") return "rejected";
    return "review";
  };
  // one review state per DOMAIN (so bucket counts match the domain list)
  const domainState = (dm: any): "review" | "accepted" | "rejected" | null => {
    const cols = domainMatches(dm); if (!cols.length) return null;
    const sts = cols.map((d: any) => reviewOf(keyOf(d.table, d.column)));
    if (sts.some((x) => x === "review")) return "review";
    if (sts.every((x) => x === "accepted")) return "accepted";
    if (sts.every((x) => x === "rejected")) return "rejected";
    return "review";                                   // mixed accepted+rejected → needs a look
  };
  const conflictOf = (colKey: string, domainCodes: Set<string>) => {
    const obs = cand.get(colKey)?.observed || []; return obs.filter((v) => !domainCodes.has(norm(v)));
  };

  // step-1 lookups with NO client match (no def with a logical field)
  const matchedKeys = new Set(defs.filter((d) => d.logical_field).map((d) => keyOf(d.table, d.column)));
  const step1Only = useMemo(() => {
    const out: Array<{ key: string; label: string; status: string; entity: string; observed: string[] }> = [];
    cand.forEach((v, key) => { if (v.status !== "REJECTED" && !matchedKeys.has(key)) out.push({ key, label: v.bn || key, status: v.status, entity: v.entity, observed: v.observed }); });
    return out.sort((a, b) => a.label.localeCompare(b.label));
  }, [cand, defs]);

  // ---- filtering of the left list ----
  const domainMatches = (dm: any) => (defsByLogical.get(dm.logical_field) || []);
  const domainPassesFilter = (dm: any) => {
    const cols = domainMatches(dm);
    if (filter === "all") return true;
    if (filter === "unmatched") return cols.length === 0;      // domains with no physical column
    if (filter === "conflict") { const set = new Set(Object.keys(dm.code_to_description || {}).map(norm)); return cols.some((d: any) => conflictOf(keyOf(d.table, d.column), set).length); }
    if (filter === "step1only") return false;                 // step1-only lives in the other section
    return cols.some((d: any) => certOf(keyOf(d.table, d.column)).cert === filter);
  };
  const bktCounts = { all: 0, review: 0, accepted: 0, rejected: 0 } as Record<string, number>;
  domains.forEach((dm) => { const st = domainState(dm); if (st) { bktCounts[st]++; bktCounts.all++; } });
  const inBucket = (dm: any) => {
    const st = domainState(dm);
    if (st === null) return filter === "unmatched";                  // no-column domains: only under the "No column" chip
    return bucket === "all" ? true : st === bucket;                  // matched domains: All = any decision state
  };
  const visDomains = domains.filter((dm) => (!q || dm.logical_field.toLowerCase().includes(q.toLowerCase())) && domainPassesFilter(dm) && inBucket(dm));
  const showStep1Only = (filter === "all" || filter === "step1only") && bucket === "review";
  const visStep1Only = showStep1Only ? step1Only.filter((s) => !q || s.label.toLowerCase().includes(q.toLowerCase()) || s.key.toLowerCase().includes(q.toLowerCase())) : [];

  // auto-select
  const selected = sel || (visDomains[0] ? { type: "domain" as const, id: visDomains[0].logical_field } : visStep1Only[0] ? { type: "col" as const, id: visStep1Only[0].key } : null);

  // ---- actions ----
  const save = async (d: MappingReview) => {
    setPending(d.key);
    try { await lookup_saveReview(entityId, [], [d]); note("Saved."); await onSaved(); }
    catch (e: any) { note(e?.detail || "Save failed", true); } finally { setPending(null); setRemapFor(null); }
  };
  const accept = (colKey: string, logical: string) => save({ key: colKey, status: "RESOLVED", logical_field: logical });
  const reject = (colKey: string) => save({ key: colKey, status: "REJECTED" });
  const reopen = (colKey: string, logical?: string) => save({ key: colKey, status: "REVIEW", logical_field: logical });
  const bothPending = domains.reduce((n, dm) => n + domainMatches(dm).filter((d: any) => certOf(keyOf(d.table, d.column)).cert === "both" && reviewOf(keyOf(d.table, d.column)) !== "accepted").length, 0);
  const acceptAll = async () => {
    const targets: MappingReview[] = [];
    domains.forEach((dm) => domainMatches(dm).forEach((d: any) => {
      const k = keyOf(d.table, d.column);
      if (certOf(k).cert === "both" && reviewOf(k) !== "accepted") targets.push({ key: k, status: "RESOLVED", logical_field: dm.logical_field });
    }));
    if (!targets.length) { note("Nothing to accept."); return; }
    setPending("all");
    try { await lookup_saveReview(entityId, [], targets); note(`Accepted ${targets.length} mapping(s).`); await onSaved(); }
    catch (e: any) { note(e?.detail || "Accept all failed", true); } finally { setPending(null); }
  };

  const matchedDomainCount = domains.filter((dm) => domainMatches(dm).length).length;
  const conflictDomainCount = domains.filter((dm) => {
    const set = new Set(Object.keys(dm.code_to_description || {}).map(norm));
    return domainMatches(dm).some((d: any) => conflictOf(keyOf(d.table, d.column), set).length);
  }).length;
  const counts = {
    all: domains.length,                                   // client domains (left list)
    both: domains.reduce((n, dm) => n + domainMatches(dm).filter((d: any) => certOf(keyOf(d.table, d.column)).cert === "both").length, 0),      // columns
    step2only: domains.reduce((n, dm) => n + domainMatches(dm).filter((d: any) => certOf(keyOf(d.table, d.column)).cert === "step2only").length, 0), // columns
    step1only: step1Only.length,                           // columns (no client data)
    conflict: conflictDomainCount,                         // domains
    unmatched: domains.length - matchedDomainCount,        // domains with 0 columns
  };

  const CHIPS: Array<{ k: Filter; label: string }> = [
    { k: "all", label: `All (${counts.all})` },
    { k: "both", label: `Both certify (${counts.both})` },
    { k: "step2only", label: `Step 2 only — not in Step 1 (${counts.step2only})` },
    { k: "step1only", label: `Step 1 only — no client data (${counts.step1only})` },
    { k: "conflict", label: `Value conflicts (${counts.conflict})` },
    { k: "unmatched", label: `No column in this app (${counts.unmatched})` },
  ];

  return (
    <div className="r2">
      <style>{CSS}</style>
      {flash && <div className={"r2-flash " + (flash.e ? "err" : "ok")}>{flash.m}</div>}

      <div className="r2-legend">
        {counts.all} client domains — <b>{matchedDomainCount}</b> match a physical column,
        <b> {counts.unmatched}</b> have none. Chips count domains (“Both certify” / “Step 2 only” count
        matched <i>columns</i>, {counts.both + counts.step2only} total). The <b>Needs review / Accepted /
        Rejected</b> buckets track the {matchedDomainCount} matched domains — no-column domains are under
        “No column in this app”.
      </div>

      <div className="r2-chips">
        {CHIPS.map((c) => <button key={c.k} className={"r2-chip " + (filter === c.k ? "on" : "")} onClick={() => setFilter(c.k)}>{c.label}</button>)}
        <div className="r2-sp" />
        {filter === "both" && <button className="r2-acceptall" disabled={busy || pending === "all" || bothPending === 0} onClick={acceptAll}>{pending === "all" ? "…" : `Accept all (${bothPending})`}</button>}
        <div className="r2-buckets">
          {(["all", "review", "accepted", "rejected"] as const).map((b) => (
            <button key={b} className={"r2-bk " + (bucket === b ? "on " : "") + b} onClick={() => setBucket(b)}>
              {b === "all" ? "All" : b === "review" ? "Needs review" : b === "accepted" ? "Accepted" : "Rejected"}<span className="r2-bkn">{bktCounts[b]}</span>
            </button>
          ))}
        </div>
        <div className="r2-search"><input placeholder="Search entity / field…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
      </div>

      <div className="r2-body">
        {/* LEFT: client domains + step1-only */}
        <div className="r2-left">
          <div className="r2-sec">Client domains ({visDomains.length})</div>
          {visDomains.map((dm) => {
            const cols = domainMatches(dm); const on = selected?.type === "domain" && selected.id === dm.logical_field;
            const anyStep2Only = cols.some((d: any) => certOf(keyOf(d.table, d.column)).cert === "step2only");
            return (
              <button key={dm.logical_field} className={"r2-li " + (on ? "on" : "")} onClick={() => setSel({ type: "domain", id: dm.logical_field })}>
                <span className="r2-li-name">{dm.logical_field}</span>
                <span className="r2-li-meta">{cols.length} col · {dm.code_count} codes {anyStep2Only && <span className="r2-warn" title="matched a column not detected in Step 1">⚠</span>}
                  {cols.length > 0 && cols.every((d: any) => reviewOf(keyOf(d.table, d.column)) === "accepted") && <span className="r2-ok" title="accepted">✓</span>}
                  {cols.some((d: any) => reviewOf(keyOf(d.table, d.column)) === "rejected") && <span className="r2-no" title="has rejected">✗</span>}</span>
              </button>
            );
          })}
          {visDomains.length === 0 && <div className="r2-empty small">No domains in this filter.</div>}

          {visStep1Only.length > 0 && <div className="r2-sec warn">Step 1 lookups · no client data ({visStep1Only.length})</div>}
          {visStep1Only.map((s) => {
            const on = selected?.type === "col" && selected.id === s.key;
            return (
              <button key={s.key} className={"r2-li " + (on ? "on" : "")} onClick={() => setSel({ type: "col", id: s.key })}>
                <span className="r2-li-name">{s.label}</span>
                <span className="r2-li-meta mono">{s.key}{s.entity ? ` · ${s.entity}` : ""}</span>
              </button>
            );
          })}
        </div>

        {/* RIGHT: detail */}
        <div className="r2-detail">
          {!selected ? <div className="r2-empty big">Select a client domain.</div>
            : selected.type === "domain" ? (
              (() => {
                const dm = domains.find((d) => d.logical_field === selected.id); if (!dm) return null;
                const cols = domainMatches(dm); const domainCodes = new Set(Object.keys(dm.code_to_description || {}).map(norm));
                return (
                  <>
                    <div className="r2-dh">{dm.logical_field}<span className="r2-dh-sub">{dm.code_count} client codes</span></div>
                    <div className="r2-excel">
                      <div className="r2-lbl">Client Excel codes</div>
                      <div className="r2-vals">
                        {Object.entries(dm.code_to_description).slice(0, 40).map(([c, d]) => <span key={c} className="r2-v"><b>{c}</b>{d ? ` ${d}` : ""}</span>)}
                        {Object.keys(dm.code_to_description).length > 40 && <span className="r2-more">+{Object.keys(dm.code_to_description).length - 40}</span>}
                      </div>
                    </div>

                    <div className="r2-lbl">Matched physical columns ({cols.length})</div>
                    {cols.length === 0 && (
                      <div className="r2-nomatch">
                        No physical column matched this domain.
                        <ColumnPicker columns={allColumns} onPick={(k) => accept(k, dm.logical_field)} busy={busy} label="Map to a column" />
                      </div>
                    )}
                    {cols.map((d: any) => {
                      const key = keyOf(d.table, d.column); const cc = certOf(key); const m = d.mapping || {};
                      const obs = cand.get(key)?.observed || []; const bad = conflictOf(key, domainCodes);
                      const rowBusy = busy || pending === key;
                      return (
                        <div key={key} className={"r2-col " + (cc.cert === "step2only" ? "s2only" : "")}>
                          <div className="r2-col-h">
                            <div><span className="r2-col-name">{d.business_name || d.column}</span><span className="r2-col-key mono">{key}</span></div>
                            <div className="r2-sp" />
                            {cc.inS1
                              ? <span className="r2-badge ok" title={`entity: ${cc.s1entity || "—"}`}>✓ Step 1 · {(cc.s1status || "").toLowerCase()}{cc.s1entity ? ` · ${cc.s1entity}` : ""}</span>
                              : <span className="r2-badge warn">✗ not detected in Step 1</span>}
                          </div>
                          <div className="r2-just">
                            {m.score != null && <b>score {m.score}</b>}{m.value_containment != null && <> · {Math.round((m.value_containment || 0) * 100)}% of the field's values are in this domain</>}
                            {m.name_similarity != null && <> · name {Math.round((m.name_similarity || 0) * 100)}%</>}
                            {d.justification && <div className="r2-just-t">{d.justification}</div>}
                          </div>
                          <div className="r2-dual">
                            <div className="r2-dualcol"><div className="r2-dl">In data (Step 1)</div>
                              <div className="r2-vals">{obs.length ? obs.slice(0, 30).map((v) => <span key={v} className={"r2-v " + (bad.includes(v) ? "bad" : "ok")}>{v}</span>) : <span className="r2-mut">no observed values</span>}</div></div>
                            <div className="r2-dualcol"><div className="r2-dl">In client Excel</div>
                              <div className="r2-vals">{Object.keys(dm.code_to_description).slice(0, 30).map((c) => <span key={c} className="r2-v gov">{c}</span>)}</div></div>
                          </div>
                          {bad.length > 0 && <div className="r2-conf">{bad.length} data value(s) not in this domain: {bad.slice(0, 8).join(", ")}</div>}
                          <div className="r2-acts">
                            {reviewOf(key) === "accepted" ? (
                              <><span className="r2-state ok">✓ Accepted</span>
                                <button className="r2-btn" disabled={rowBusy} onClick={() => reopen(key, dm.logical_field)}>Reopen</button></>
                            ) : reviewOf(key) === "rejected" ? (
                              <><span className="r2-state no">✗ Rejected</span>
                                <button className="r2-btn" disabled={rowBusy} onClick={() => reopen(key, dm.logical_field)}>Reopen</button></>
                            ) : (
                              <><button className="r2-btn pri" disabled={rowBusy} onClick={() => accept(key, dm.logical_field)}>Accept lookup</button>
                                <button className="r2-btn" disabled={rowBusy} onClick={() => setRemapFor(remapFor === key ? null : key)}>Remap…</button>
                                <button className="r2-btn warn" disabled={rowBusy} onClick={() => reject(key)}>Reject</button></>
                            )}
                          </div>
                          {remapFor === key && <ColumnPicker columns={allColumns.filter((c) => c.key !== key)} onPick={(k) => accept(k, dm.logical_field)} busy={rowBusy} label="Attach this domain to column" />}
                        </div>
                      );
                    })}
                  </>
                );
              })()
            ) : (
              (() => {
                const cur = step1Only.find((x) => x.key === selected.id); if (!cur) return <div className="r2-empty big">—</div>;
                return (
                  <>
                    <div className="r2-dh">{cur.label}<span className="r2-dh-sub mono">{cur.key}</span></div>
                    <div className="r2-badge ok" style={{ display: "inline-block", marginBottom: 10 }}>✓ Step 1 · {cur.status.toLowerCase()}{cur.entity ? ` · ${cur.entity}` : ""}</div>
                    <div className="r2-nomatch">This lookup has <b>no client data</b> in the uploaded workbook. It will be governed from profiling evidence in Step 3.</div>
                    <div className="r2-lbl">Observed values (data)</div>
                    <div className="r2-vals">{cur.observed.slice(0, 40).map((v) => <span key={v} className="r2-v ok">{v}</span>)}{cur.observed.length > 40 && <span className="r2-more">+{cur.observed.length - 40}</span>}</div>
                  </>
                );
              })()
            )}
        </div>
      </div>

      <div className="r2-footer">
        <span>No client data for some fields? Accept profile values in <button className="r2-link" onClick={onGoDiscover}>Discovery</button>.</span>
        <div className="r2-sp" />
        <button className="r2-btn dark" disabled={busy} onClick={onReconcile}>Fold into entities &amp; continue →</button>
      </div>
    </div>
  );
}

function ColumnPicker({ columns, onPick, busy, label }: { columns: Array<{ key: string; label: string }>; onPick: (k: string) => void; busy: boolean; label: string }) {
  const [v, setV] = useState("");
  return (
    <div className="r2-pick">
      <input list="r2-cols" className="r2-in" placeholder={`${label}…`} value={v} onChange={(e) => setV(e.target.value)} />
      <datalist id="r2-cols">{columns.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}</datalist>
      <button className="r2-btn pri" disabled={busy || !columns.find((c) => c.key === v.toUpperCase())} onClick={() => onPick(v.toUpperCase())}>Map</button>
    </div>
  );
}

const CSS = `
.r2{--ink:#14202b;--surface:#fff;--surface2:#f7f9f7;--line:#dde3df;--line2:#e9ede9;--tx:#182430;--tx2:#5d6d68;--tx3:#8a9791;
  --gov:#0e8a6e;--gov-bg:#e2f2ec;--wait:#bd7a15;--wait-bg:#f7ecd6;--warn:#c23c28;--warn-bg:#f7e0da;--infer:#4a51c0;--infer-bg:#e6e7f7;
  font-family:'Inter',system-ui,sans-serif;color:var(--tx);font-size:14px;margin-top:14px}
.r2 *{box-sizing:border-box}.r2 .mono,.r2-col-key,.r2-v{font-family:'IBM Plex Mono',ui-monospace,monospace}
.r2-flash{padding:9px 13px;border-radius:8px;font-size:13px;margin-bottom:12px;background:var(--gov-bg);color:var(--gov)}
.r2-flash.err{background:var(--warn-bg);color:var(--warn)}
.r2-legend{font-size:12px;color:var(--tx2);background:var(--surface2);border:1px solid var(--line);border-radius:9px;padding:8px 12px;margin-bottom:10px}
.r2-legend b{color:var(--tx)}.r2-legend i{font-style:italic}
.r2-chips{display:flex;gap:8px;align-items:center;margin-bottom:12px;flex-wrap:wrap}

.r2-buckets{display:inline-flex;align-items:center;background:var(--surface2);border:1px solid var(--line);border-radius:9px;padding:3px;gap:2px;margin-right:10px}
.r2-bk{border:none;background:transparent;padding:6px 12px;font-size:12.5px;line-height:1;cursor:pointer;color:var(--tx2);font-family:inherit;border-radius:7px;display:inline-flex;align-items:center;gap:7px;font-weight:500;transition:background .12s,color .12s}
.r2-bk:hover{color:var(--tx)}
.r2-bk.on{background:var(--surface);box-shadow:0 1px 2px rgba(16,32,43,.10);font-weight:600}
.r2-bk.on.all{color:var(--tx)} .r2-bk.on.review{color:var(--infer)} .r2-bk.on.accepted{color:var(--gov)} .r2-bk.on.rejected{color:var(--warn)}
.r2-bkn{font-family:'IBM Plex Mono',ui-monospace,monospace;font-size:11px;min-width:16px;text-align:center;background:#00000010;color:var(--tx2);border-radius:20px;padding:1px 7px;font-weight:600}
.r2-bk.on.all .r2-bkn{background:var(--surface2);color:var(--tx2)}
.r2-bk.on.review .r2-bkn{background:var(--infer-bg);color:var(--infer)}
.r2-bk.on.accepted .r2-bkn{background:var(--gov-bg);color:var(--gov)}
.r2-bk.on.rejected .r2-bkn{background:var(--warn-bg);color:var(--warn)}
.r2-acceptall{border:1px solid var(--gov);background:var(--gov);color:#fff;border-radius:8px;padding:7px 14px;font-size:12.5px;font-weight:600;line-height:1;cursor:pointer;font-family:inherit;margin-right:10px;box-shadow:0 1px 2px rgba(22,101,52,.18)}
.r2-acceptall:hover:not(:disabled){filter:brightness(.95)}
.r2-acceptall:disabled{opacity:.45;cursor:default;box-shadow:none}
.r2-state{font-size:12.5px;font-weight:600;padding:6px 12px;border-radius:7px;display:inline-flex;align-items:center;gap:5px}
.r2-state.ok{background:var(--gov-bg);color:var(--gov);border:1px solid #cdead7}
.r2-state.no{background:var(--warn-bg);color:var(--warn);border:1px solid #eccaca}
.r2-ok{color:var(--gov);font-weight:700;margin-left:5px}
.r2-no{color:var(--warn);font-weight:700;margin-left:5px}
.r2-chip{border:1px solid var(--line);background:var(--surface);border-radius:20px;padding:6px 12px;font-size:12px;cursor:pointer;color:var(--tx2);font-family:inherit}
.r2-chip:hover{border-color:var(--tx3)}.r2-chip.on{background:var(--ink);color:#fff;border-color:var(--ink)}
.r2-sp{flex:1}
.r2-search{min-width:180px}.r2-search input{border:1px solid var(--line);border-radius:9px;padding:7px 11px;font-size:13px;outline:none;width:100%;font-family:inherit}
.r2-body{display:grid;grid-template-columns:320px 1fr;gap:14px;min-height:460px}
.r2-left{border:1px solid var(--line);border-radius:12px;background:var(--surface);overflow-y:auto;max-height:640px}
.r2-sec{font-size:11px;letter-spacing:.06em;text-transform:uppercase;color:var(--tx2);font-weight:600;padding:11px 13px 6px}
.r2-sec.warn{color:var(--warn)}
.r2-li{display:flex;flex-direction:column;gap:2px;width:100%;text-align:left;border:none;border-bottom:1px solid var(--line2);background:transparent;padding:9px 13px;cursor:pointer;font-family:inherit}
.r2-li:hover{background:var(--surface2)}.r2-li.on{background:var(--infer-bg)}
.r2-li-name{font-size:12.5px;font-weight:600}
.r2-li-meta{font-size:11px;color:var(--tx3)}
.r2-warn{color:var(--warn)}
.r2-detail{border:1px solid var(--line);border-radius:12px;background:var(--surface);padding:16px 18px;overflow-y:auto;max-height:640px}
.r2-dh{font-size:18px;font-weight:600;font-family:'Space Grotesk',Inter,sans-serif;display:flex;align-items:baseline;gap:10px}
.r2-dh-sub{font-size:12px;color:var(--tx3);font-weight:400}
.r2-lbl{font-size:11px;letter-spacing:.06em;text-transform:uppercase;color:var(--tx2);font-weight:600;margin:14px 0 7px}
.r2-excel{margin-top:12px}
.r2-vals{display:flex;flex-wrap:wrap;gap:5px}
.r2-v{font-size:11px;background:var(--surface2);border:1px solid var(--line2);border-radius:5px;padding:2px 7px}
.r2-v b{color:var(--gov)}.r2-v.ok{border-color:#bfe0d4;color:var(--gov)}.r2-v.bad{border-color:#e6b5aa;color:var(--warn);font-weight:600}.r2-v.gov{color:var(--tx2)}
.r2-more{font-size:11px;color:var(--tx3)}.r2-mut{font-size:11.5px;color:var(--tx3);font-style:italic}
.r2-col{border:1px solid var(--line);border-radius:11px;padding:12px 13px;margin-bottom:10px}
.r2-col.s2only{border-left:4px solid var(--warn)}
.r2-col-h{display:flex;align-items:center;gap:9px}
.r2-col-name{font-size:14px;font-weight:600}.r2-col-key{font-size:11px;color:var(--tx3);margin-left:8px}
.r2-badge{font-size:10.5px;font-weight:600;padding:3px 9px;border-radius:6px;white-space:nowrap}
.r2-badge.ok{background:var(--gov-bg);color:var(--gov)}.r2-badge.warn{background:var(--warn-bg);color:var(--warn)}
.r2-just{font-size:12px;color:var(--tx2);margin:8px 0}
.r2-just-t{margin-top:5px;color:var(--tx2)}
.r2-dual{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin:8px 0}
.r2-dualcol{background:var(--surface2);border:1px solid var(--line2);border-radius:8px;padding:9px}
.r2-dl{font-size:10.5px;letter-spacing:.05em;text-transform:uppercase;color:var(--tx2);font-weight:600;margin-bottom:6px}
.r2-conf{font-size:12px;color:var(--warn);background:var(--warn-bg);border-radius:7px;padding:6px 10px;margin-bottom:8px}
.r2-acts{display:flex;gap:7px;flex-wrap:wrap}
.r2-btn{border:1px solid var(--line);background:var(--surface);border-radius:7px;padding:6px 12px;font-size:12.5px;font-weight:500;cursor:pointer;font-family:inherit;color:var(--tx)}
.r2-btn:hover{border-color:var(--tx2);background:var(--surface2)}
.r2-btn.pri{background:var(--gov);border-color:var(--gov);color:#fff}.r2-btn.pri:hover{background:#0b7560}
.r2-btn.warn{color:var(--warn)}.r2-btn.warn:hover{border-color:var(--warn);background:var(--warn-bg)}
.r2-btn.dark{background:var(--ink);color:#fff;border-color:var(--ink)}.r2-btn.dark:hover{background:#20303e}
.r2-btn:disabled{opacity:.5;cursor:default}
.r2-nomatch{font-size:12.5px;color:var(--tx2);background:var(--surface2);border:1px solid var(--line2);border-radius:9px;padding:11px;margin:8px 0}
.r2-pick{display:flex;gap:7px;margin-top:9px}
.r2-in{flex:1;border:1px solid var(--line);border-radius:7px;padding:7px 10px;font-size:12.5px;outline:none;font-family:'IBM Plex Mono'}
.r2-footer{display:flex;align-items:center;gap:11px;margin-top:14px;padding-top:13px;border-top:1px dashed var(--line);font-size:12.5px;color:var(--tx2);flex-wrap:wrap}
.r2-link{border:none;background:none;color:var(--infer);cursor:pointer;text-decoration:underline;font-family:inherit;font-size:inherit;padding:0}
.r2-empty{color:var(--tx3)}.r2-empty.small{padding:14px;font-size:12.5px}.r2-empty.big{display:grid;place-items:center;height:100%}
@media(max-width:860px){.r2-body{grid-template-columns:1fr}.r2-dual{grid-template-columns:1fr}}
`;
